/**
 * FunnelSection — Bezoekers → Calls → Leads → Omzet funnel-strip.
 *
 * Sinds 14 aug 2026 zonder "Funnel Diagnose"-sectie: terugkerende bezoekers
 * staat als metaregel in de BEZOEKERS-kaart. De source attributie, de
 * pagina-tabellen én de attributie-gezondheid (match rate/confidence/kwaliteit)
 * staan in AttributionSection, lager op de pagina.
 */

import React from 'react';
import { Users, Phone, CheckCircle, Euro, Info } from 'lucide-react';
import { formatCurrency, formatNumber } from '../../utils/dashboardUtils';
import { TRACKING_START_DATE } from '../../utils/trackingHelpers';

// Compacte stat-rij: icoon links, label + meta in het midden, waarde rechts.
// meta = array van regels; die staan ónder elkaar zodat de kaart smal blijft
// (stap-ratio op regel 1, end-to-end metric op regel 2).
const FunnelStep = ({ icon: Icon, label, value, meta, metaTitle, isLast }) => (
  <div className="funnel-step-wrapper">
    <div className="kpi-card funnel-step-card">
      <div className="funnel-step-icon">
        <Icon size={18} strokeWidth={2.5} />
      </div>
      <div className="funnel-step-meta" title={metaTitle || undefined}>
        <span className="funnel-step-label">{label}</span>
        {(Array.isArray(meta) ? meta : (meta ? [meta] : [])).map((line, i) => (
          <span key={i} className="funnel-step-ratio">{line}</span>
        ))}
      </div>
      <div className="funnel-step-value">{value}</div>
    </div>
    {!isLast && <div className="funnel-arrow">→</div>}
  </div>
);

// nl-NL percentage met 1 decimaal (18.7 -> "18,7%").
const fmtPct1 = (v) => (v == null ? null
  : `${v.toLocaleString('nl-NL', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`);

const FunnelSection = ({
  funnelData, matchBreakdown, attribution, loading,
  calls = 0, leads = 0, revenue = 0,
  customerFilterActive = false, rangeStart = null,
}) => {
  // Skeleton alléén bij de allereerste load: bij de 60s-auto-refresh blijft de
  // strip gewoon staan en veranderen alleen de cijfers.
  if (loading && !funnelData) {
    return (
      <div className="mb-6 lg:mb-8">
        <div className="funnel-row" style={{ minHeight: '68px' }}>
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="funnel-step-wrapper">
              <div className="kpi-card funnel-step-card animate-pulse" style={{ minHeight: '68px' }} />
              {i < 3 && <div className="funnel-arrow">→</div>}
            </div>
          ))}
        </div>
      </div>
    );
  }

  const { sessions = 0, botSessions = 0 } = funnelData || {};
  const visitors = attribution?.visitors || null;

  const pct1 = (num, den) => (den > 0 ? fmtPct1((num / den) * 100) : null);
  const eur2 = (v) => new Intl.NumberFormat('nl-NL', {
    style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(v);

  // De teller is unieke (nieuwe) bezoekers; de metaregels tonen het totaal
  // aantal bezoeken (sessies, elke terugkomst telt mee) en het aandeel
  // terugkerende bezoekers. Bots tellen nergens in mee; de botregel verschijnt
  // alleen als er echt iets gefilterd is.
  const returningPct = visitors && visitors.total > 0 ? pct1(visitors.returning, visitors.total) : null;
  const visitorsMeta = [
    visitors && visitors.totalVisits > 0 && `${formatNumber(visitors.totalVisits)} sessies`,
    returningPct && `${returningPct} terugkerend`,
    botSessions > 0 && `${formatNumber(botSessions)} bots gefilterd`,
  ].filter(Boolean);
  const visitorsMetaTitle = [
    visitors && visitors.totalVisits > 0 && 'Sessies = alle bezoeken van deze bezoekers, elke terugkomst telt mee.',
    returningPct && `${formatNumber(visitors.returning)} van ${formatNumber(visitors.total)} bezoekers kwam vaker dan 1x (visit_count > 1).`,
    'Botverkeer wordt niet meegeteld.',
  ].filter(Boolean).join(' ');

  // Tel-klikken zijn geen aparte funnel-stap meer (calls > klikken kan, want
  // niet elke beller klikt de getrackte tel:-link). Ze staan als sub-metric in
  // de meta-regel van CALLS, met de uitleg in de tooltip.
  const clickMatchedCalls = matchBreakdown
    ? (matchBreakdown.timestamp_phone || 0) + (matchBreakdown.timestamp || 0)
    : null;

  const callsMeta = [
    pct1(calls, sessions) && `${pct1(calls, sessions)} van bezoekers`,
    clickMatchedCalls !== null && calls > 0 && `${formatNumber(clickMatchedCalls)} via klik`,
  ].filter(Boolean);
  const callsMetaTitle = clickMatchedCalls !== null && calls > 0
    ? `${formatNumber(clickMatchedCalls)} van ${formatNumber(calls)} calls kwam via een getrackte tel-klik (${((clickMatchedCalls / calls) * 100).toFixed(0)}%)`
    : null;

  const leadsMeta = [
    pct1(leads, calls) && `${pct1(leads, calls)} van calls`,
    pct1(leads, sessions) && `${pct1(leads, sessions)} van bezoekers`,
  ].filter(Boolean);

  const revenueMeta = [
    leads > 0 && `€${(revenue / leads).toFixed(0)} per lead`,
    sessions > 0 && `${eur2(revenue / sessions)} per bezoeker`,
  ].filter(Boolean);

  // Tracking begint op TRACKING_START_DATE; eerdere periodes leveren stil lage
  // bezoekerscijfers op — dat melden we expliciet.
  const beforeTrackingStart = rangeStart
    && new Date(rangeStart).getTime() < new Date(`${TRACKING_START_DATE}T00:00:00+02:00`).getTime();

  return (
    <div className="mb-6 lg:mb-8">
      {/* Funnel steps */}
      <div className="funnel-row">
        <FunnelStep
          icon={Users}
          label="BEZOEKERS"
          value={formatNumber(sessions)}
          meta={visitorsMeta.length ? visitorsMeta : null}
          metaTitle={visitorsMetaTitle}
          isLast={false}
        />
        <FunnelStep
          icon={Phone}
          label="CALLS"
          value={formatNumber(calls)}
          meta={callsMeta}
          metaTitle={callsMetaTitle}
          isLast={false}
        />
        <FunnelStep
          icon={CheckCircle}
          label="LEADS"
          value={formatNumber(leads)}
          meta={leadsMeta}
          isLast={false}
        />
        <FunnelStep
          icon={Euro}
          label="OMZET"
          value={formatCurrency(revenue)}
          meta={revenueMeta}
          isLast={true}
        />
      </div>

      {(customerFilterActive || beforeTrackingStart) && (
        <div className="funnel-note-stack">
          {customerFilterActive && (
            <div className="funnel-note">
              <Info size={13} />
              <span>Bezoekers en bron-attributie tonen alle klanten samen; het klantfilter geldt daar niet.</span>
            </div>
          )}
          {beforeTrackingStart && (
            <div className="funnel-note">
              <Info size={13} />
              <span>Tracking-data is beschikbaar vanaf 26 april 2026; het deel van de periode daarvóór telt hier niet mee.</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FunnelSection;
